<template>
  <div class="custom-table-card bg-white overflow-hidden mb-4">
    <!-- Header: Title and Icon -->
    <div class="table-header-section pt-4 px-4 pb-2">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <div class="d-flex align-items-center gap-2">
          <i v-if="titleIcon" :class="titleIcon + ' text-dark'" style="font-size: 1.25rem;"></i>
          <h4 class="fw-bold text-dark m-0">{{ title }}</h4>
        </div>
      </div>
      
      <!-- Subtitle Description -->
      <p class="text-muted mb-2 small">{{ subtitle }}</p>

      <!-- Emulated Tab Design with Dark Underline and Full-Width Bottom border -->
      <div v-if="tabs && tabs.length > 0" class="tabs-container mb-4">
        <ul class="nav nav-tabs border-bottom-0 gap-4" role="tablist">
          <li v-for="tab in tabs" :key="getTabKey(tab)" class="nav-item" role="presentation">
            <button 
              class="nav-link border-0 bg-transparent px-0 py-2 position-relative" 
              :class="{ 'active-tab-style text-dark fw-bold': activeTab === getTabKey(tab), 'text-muted': activeTab !== getTabKey(tab) }"
              type="button" 
              @click="switchTab(getTabKey(tab))"
            >
              {{ getTabLabel(tab) }}
              <span v-if="activeTab === getTabKey(tab)" class="active-tab-line"></span>
            </button>
          </li>
        </ul>
      </div>

      <!-- Aligned Input Controls & Spacing -->
      <div class="d-flex align-items-center justify-content-between gap-3 mb-3 mt-3">
        <!-- Left Side: Total count and Search Input (UX Priority 1) -->
        <div class="d-flex align-items-center gap-3 flex-grow-1" style="max-width: 50%;">
          <span v-if="tableData && tableData.pagination.total > 0" class="fw-semibold text-dark text-nowrap" style="font-size: 0.95rem;">
            {{ tableData.pagination.total }} {{ title }}
          </span>
          <div v-if="enableSearch" class="input-group search-input-group border">
            <span class="input-group-text border-0 bg-transparent">
              <i class="fa fa-search text-muted small"></i>
            </span>
            <input
              v-model="searchQuery"
              type="text"
              class="form-control border-0 bg-transparent py-1"
              placeholder="Search"
              @input="debounceSearch"
            />
          </div>
        </div>

        <!-- Right Side: Date Picker, Dropdowns, Export & Customize Columns (UX Priority 2) -->
        <div class="d-flex align-items-center gap-2">
          <!-- Inline Date Range Filter -->
          <div v-if="enableDateFilter" class="d-flex align-items-center gap-2 me-2">
            <input
              v-model="startDate"
              type="date"
              class="form-control date-range-picker"
              @change="applyFilters"
            />
            <span class="small text-muted">-</span>
            <input
              v-model="endDate"
              type="date"
              class="form-control date-range-picker"
              @change="applyFilters"
            />
          </div>

          <!-- Dynamic Dropdowns as individual filter buttons -->
          <div v-for="(filterOptions, filterKey) in serverFilters" :key="filterKey" class="dropdown">
            <button 
              class="btn btn-outline-secondary dropdown-toggle d-flex align-items-center justify-content-between gap-2 filter-btn rounded-2"
              type="button" 
              :id="`dropdown-${filterKey}`"
              data-bs-toggle="dropdown" 
              aria-expanded="false"
            >
              <span class="fw-normal">{{ getFilterDisplayText(filterKey, filterOptions) }}</span>
              <span v-if="selectedFilters[filterKey]" class="badge bg-light text-dark p-1 ms-1">
                {{ Array.isArray(selectedFilters[filterKey]) ? selectedFilters[filterKey].length : 1 }}
              </span>
            </button>
            <ul class="dropdown-menu p-2 shadow border-0" :aria-labelledby="`dropdown-${filterKey}`" style="min-width: 200px; border-radius: 8px;">
              <li v-if="selectedFilters[filterKey]">
                <button class="dropdown-item text-danger d-flex align-items-center" @click="clearFilter(filterKey)">
                  <i class="fa fa-times me-2"></i>Clear Filter
                </button>
                <hr class="dropdown-divider">
              </li>
              <li v-for="option in filterOptions" :key="option.value || option">
                <button
                  class="dropdown-item d-flex align-items-center justify-content-between rounded-2"
                  :class="{ 'active': isFilterSelected(filterKey, option.value || option) }"
                  @click="setSingleFilter(filterKey, option.value || option)"
                >
                  <span>{{ option.label || option }}</span>
                  <span v-if="option.count !== undefined" class="badge bg-light text-muted">{{ option.count }}</span>
                </button>
              </li>
              <li v-if="filterOptions.length === 0">
                <span class="dropdown-item-text text-muted small">No options available</span>
              </li>
            </ul>
          </div>

          <!-- Customize Columns Dropdown triggered by bullet list icon button -->
          <div v-if="showSettings" class="dropdown">
            <button 
              class="btn btn-outline-secondary d-flex align-items-center justify-content-center filter-icon-btn" 
              type="button" 
              id="table-settings-dropdown" 
              data-bs-toggle="dropdown" 
              aria-expanded="false" 
              title="Customize columns"
            >
              <i class="fas fa-list-ul"></i>
            </button>
            <ul class="dropdown-menu dropdown-menu-end p-3 shadow border-0 customize-columns-dropdown-menu" aria-labelledby="table-settings-dropdown" style="min-width: 260px; border-radius: 8px;" @click.stop>
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="text-dark fw-bold m-0 small text-uppercase tracking-wider">Customize columns</h6>
                <button class="btn btn-link btn-sm text-primary p-0 border-0 text-decoration-none small" @click="resetColumnVisibility">Reset</button>
              </div>
              <hr class="my-2">
              <li 
                v-for="(column, idx) in localColumns" 
                :key="column.key" 
                class="py-1 d-flex align-items-center gap-2 dropdown-item-custom"
                draggable="true"
                @dragstart="onDragStart($event, idx)"
                @dragover.prevent
                @drop="onDrop($event, idx)"
              >
                <i class="fas fa-grip-vertical text-muted opacity-50 cursor-grab" style="font-size: 0.8rem;"></i>
                <div class="form-check m-0 d-flex align-items-center gap-2">
                  <input class="form-check-input mt-0 shadow-none" type="checkbox" :id="'col-vis-' + column.key" v-model="visibleColumns[column.key]">
                  <label class="form-check-label small text-dark user-select-none" :for="'col-vis-' + column.key">{{ column.label }}</label>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- Active Filters Display -->
      <div v-if="hasActiveFilters" class="mt-2 d-flex flex-wrap gap-2 pb-2">
        <span class="badge filter-pill" v-if="searchQuery">
          Search: "{{ searchQuery }}"
          <i class="fa fa-times ms-1 cursor-pointer" @click="clearSearch"></i>
        </span>
        <span class="badge filter-pill" v-if="startDate">
          From: {{ formatFilterDate(startDate) }}
          <i class="fa fa-times ms-1 cursor-pointer" @click="clearStartDate"></i>
        </span>
        <span class="badge filter-pill" v-if="endDate">
          To: {{ formatFilterDate(endDate) }}
          <i class="fa fa-times ms-1 cursor-pointer" @click="clearEndDate"></i>
        </span>
        <template v-for="(value, key) in selectedFilters" :key="key">
          <span v-if="value" class="badge filter-pill">
            {{ formatFilterLabel(key) }}: {{ getFilterOptionLabel(key, value) }}
            <i class="fa fa-times ms-1 cursor-pointer" @click="clearFilter(key)"></i>
          </span>
        </template>
      </div>
    </div>

    <!-- Loading State (Initial Fetch Only) -->
    <div v-if="loading && !tableData" class="text-center py-5">
      <div class="spinner-border text-primary spinner-sm" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-2 small">Loading data...</p>
    </div>

    <!-- Top Progress Bar Loader for Background updates -->
    <div class="position-relative" style="height: 3px; z-index: 10;">
      <div v-if="loading && tableData" class="loading-progress-bar"></div>
    </div>

    <!-- Table Container -->
    <div v-if="tableData" class="table-responsive border-top border-bottom px-0" :class="{ 'table-updating': loading }">
      <table class="table table-hover align-middle mb-0 custom-styled-table">
        <thead>
          <tr class="bg-light-header">
            <!-- Global Select Checkbox -->
            <th v-if="showCheckboxes" class="px-4 py-3 align-middle" style="width: 48px;">
              <input type="checkbox" class="form-check-input" :checked="isAllSelected" @change="toggleSelectAll" />
            </th>
            
            <th
              v-for="column in localColumns"
              v-show="isColumnVisible(column.key)"
              :key="column.key"
              class="text-dark small fw-semibold px-4 py-3 cursor-pointer user-select-none"
              @click="sortBy(column.key)"
            >
              <div class="d-flex align-items-center justify-content-between">
                <span>{{ column.label }}</span>
                <div class="ms-2 d-inline-block">
                  <i
                    v-if="currentSort.column === column.key"
                    :class="{
                      'fas fa-chevron-up': currentSort.direction === 'asc',
                      'fas fa-chevron-down': currentSort.direction === 'desc'
                    }"
                    class="text-primary small"
                  ></i>
                  <i v-else class="fas fa-sort text-muted opacity-25 small"></i>
                </div>
              </div>
            </th>
            <th v-if="hasActions" class="text-end text-muted small fw-semibold px-4 py-3" style="width: 80px;">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in currentData" :key="getRowKey(item, index)" class="table-row-hover">
            <!-- Row Checkbox -->
            <td v-if="showCheckboxes" class="px-4 py-3 align-middle">
              <input type="checkbox" class="form-check-input" :checked="isRowSelected(item)" @change="toggleRowSelection(item)" />
            </td>
            
            <!-- Dynamic Slots or Default Cells -->
            <slot name="row" :item="item" :index="index" :visible="isColumnVisible">
              <td v-for="column in localColumns" v-show="isColumnVisible(column.key)" :key="column.key" class="px-4 py-3">
                <span v-if="column.type === 'date'" class="text-muted small">
                  {{ formatDate(getNestedValue(item, column.key)) }}
                </span>
                <span v-else-if="column.type === 'currency'" class="small text-dark font-monospace">
                  ₦{{ formatCurrency(getNestedValue(item, column.key)) }}
                </span>
                <span v-else-if="column.type === 'badge'" 
                      :class="`badge ${getBadgeClass(getNestedValue(item, column.key))} rounded-pill px-3 py-2`">
                  {{ getNestedValue(item, column.key) }}
                </span>
                <span v-else class="small text-muted">
                  {{ getNestedValue(item, column.key) }}
                </span>
              </td>
            </slot>
            
            <!-- Row Actions Far Right -->
            <td v-if="hasActions" class="px-4 py-3 align-middle text-end">
              <slot name="actions" :item="item" :index="index">
                <div class="dropdown">
                  <button 
                    class="btn btn-link text-muted p-1 border-0 rounded-circle hover-bg-light" 
                    type="button" 
                    data-bs-toggle="dropdown"
                    style="width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;"
                  >
                    <i class="fas fa-ellipsis-h"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end shadow border-0" style="border-radius: 8px;">
                    <li><a class="dropdown-item" href="#" @click.prevent="$emit('view', item)"><i class="fa fa-eye me-2 small"></i>View</a></li>
                    <li><a class="dropdown-item" href="#" @click.prevent="$emit('edit', item)"><i class="fas fa-pencil-alt me-2 small"></i>Edit</a></li>
                    <li><hr class="dropdown-divider"></li>
                    <li><a class="dropdown-item text-danger" href="#" @click.prevent="$emit('delete', item)"><i class="fa fa-trash me-2 small"></i>Delete</a></li>
                  </ul>
                </div>
              </slot>
            </td>
          </tr>
          
          <!-- Empty Data State -->
          <tr v-if="!currentData || currentData.length === 0">
            <td :colspan="localColumns.length + (hasActions ? 1 : 0) + (showCheckboxes ? 1 : 0)" class="py-5 text-muted border-0">
              <div class="d-flex flex-column align-items-center justify-content-center text-center w-100">
                <slot name="empty">
                  <i class="fa fa-inbox fs-2 mb-3 opacity-25"></i>
                  <div>
                    <p class="mb-1 fw-medium text-dark">No data found</p>
                    <small v-if="hasActiveFilters" class="text-muted small">Try adjusting your search criteria or filters</small>
                  </div>
                </slot>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Emulated Footer Pagination from screenshot -->
    <div v-if="!loading && tableData && tableData.pagination.total > 0" class="d-flex align-items-center justify-content-between p-4">
      <!-- Left: Total results and pages count -->
      <div class="text-muted small">
        {{ tableData.pagination.total }} Results | Page {{ tableData.pagination.current_page }} of {{ tableData.pagination.last_page }}
      </div>
      
      <!-- Center: Square box controls [<<] [<] [Page Box] [>] [>>] -->
      <div class="d-flex align-items-center gap-1">
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="tableData.pagination.current_page === 1"
          @click="goToPage(1)"
        >
          <i class="fas fa-angle-double-left"></i>
        </button>
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="tableData.pagination.current_page === 1"
          @click="goToPage(tableData.pagination.current_page - 1)"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <span class="border rounded px-3 py-1 bg-white fw-bold small text-dark mx-2" style="border-color: #d1d5db; min-width: 32px; text-align: center;">
          {{ tableData.pagination.current_page }}
        </span>
        
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="tableData.pagination.current_page === tableData.pagination.last_page"
          @click="goToPage(tableData.pagination.current_page + 1)"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
        <button 
          class="btn btn-link btn-sm text-muted p-1 text-decoration-none pagination-arrow-btn" 
          :disabled="tableData.pagination.current_page === tableData.pagination.last_page"
          @click="goToPage(tableData.pagination.last_page)"
        >
          <i class="fas fa-angle-double-right"></i>
        </button>
      </div>

      <!-- Right: Switch items per page dynamically -->
      <div class="d-flex align-items-center gap-2">
        <span class="small text-muted text-nowrap">Items per page:</span>
        <button 
          v-for="size in [10, 20, 50, 100]" 
          :key="size" 
          class="btn btn-link btn-sm p-0 text-decoration-none items-per-page-toggle"
          :class="itemsPerPage === size ? 'fw-bold text-dark text-underline' : 'text-muted'"
          @click="setItemsPerPage(size)"
        >
          {{ size }}
        </button>
      </div>
    </div>

    <!-- Bottom Alert Block -->
    <div v-if="alertMessage" class="px-4 pb-4">
      <div class="alert-banner d-flex align-items-center gap-3 p-3">
        <div class="alert-icon-container">
          <i class="fas fa-lightbulb"></i>
        </div>
        <span class="alert-text small fw-medium">{{ alertMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue, toNative, Prop, Watch } from 'vue-facing-decorator'

interface TableColumn {
  key: string
  label: string
  type?: 'text' | 'date' | 'currency' | 'badge'
  sortable?: boolean
  searchable?: boolean
}

interface LaravelPaginationResponse {
  data: any[]
  pagination: {
    current_page: number
    per_page: number
    total: number
    last_page: number
    from: number
    to: number
    has_more_pages: boolean
  }
  filters: Record<string, any[]>
  meta: {
    search_term: string
    sort_column: string
    sort_direction: string
  }
}

@Component({
  emits: ['view', 'edit', 'delete', 'data-change', 'sort', 'tab-change']
})
class EnhancedTableComponent extends Vue {
  @Prop({ type: String, required: true })
  readonly apiEndpoint!: string

  @Prop({ type: Array, required: true })
  readonly columns!: TableColumn[]

  @Prop({ type: String, default: 'Data Table' })
  readonly title!: string

  @Prop({ type: String, default: '' })
  readonly titleIcon!: string

  @Prop({ type: String, default: 'Overview of data' })
  readonly subtitle!: string

  @Prop({ type: Boolean, default: true })
  readonly enableSearch!: boolean

  @Prop({ type: Boolean, default: false })
  readonly enableDateFilter!: boolean

  @Prop({ type: Boolean, default: false })
  readonly hasActions!: boolean

  @Prop({ type: Boolean, default: false })
  readonly showCheckboxes!: boolean

  @Prop({ type: Boolean, default: true })
  readonly showSettings!: boolean

  @Prop({ type: String, default: '' })
  readonly alertMessage!: string

  @Prop({ type: Array, default: () => [] })
  readonly tabs!: (string | { key: string; label: string })[]

  @Prop({ type: String, default: 'id' })
  readonly rowKey!: string

  @Prop({ type: Number, default: 10 })
  readonly defaultItemsPerPage!: number

  @Prop({ type: Object, default: () => ({}) })
  readonly additionalParams!: Record<string, any>

  // Reactive data
  tableData: LaravelPaginationResponse | null = null
  loading = false
  searchQuery = ''
  startDate = ''
  endDate = ''
  itemsPerPage = this.defaultItemsPerPage
  selectedFilters: Record<string, any> = {}
  currentSort = { column: '', direction: 'asc' }
  searchTimeout: number | null = null
  visibleColumns: Record<string, boolean> = {}
  selectedRows: any[] = []
  activeTab = ''
  localColumns: TableColumn[] = []
  draggedIndex: number | null = null

  mounted() {
    this.itemsPerPage = this.defaultItemsPerPage
    this.localColumns = [...this.columns]
    
    // Initialize columns visibility map
    this.localColumns.forEach(col => {
      this.visibleColumns[col.key] = true
    })

    // Initialize first tab as active if provided
    if (this.tabs && this.tabs.length > 0) {
      this.activeTab = this.getTabKey(this.tabs[0])
    }

    this.fetchData()
  }

  @Watch('columns', { deep: true })
  onColumnsPropChange(newVal: TableColumn[]) {
    this.localColumns = [...newVal]
  }

  get currentData(): any[] {
    return this.tableData?.data || []
  }

  get serverFilters(): Record<string, any[]> {
    return this.tableData?.filters || {}
  }

  get hasActiveFilters(): boolean {
    const hasDropdownFilters = Object.values(this.selectedFilters).some(filter => {
      if (Array.isArray(filter)) return filter.length > 0
      return filter !== null && filter !== undefined && filter !== ''
    })
    return !!(this.searchQuery || this.startDate || this.endDate || hasDropdownFilters)
  }

  get isAllSelected(): boolean {
    return this.currentData.length > 0 && this.selectedRows.length === this.currentData.length
  }

  // Watchers
  @Watch('itemsPerPage')
  onItemsPerPageChange() {
    this.applyFilters()
  }

  // Column Visibility Handlers
  isColumnVisible(key: string): boolean {
    return this.visibleColumns[key] !== false
  }

  resetColumnVisibility() {
    this.localColumns.forEach(col => {
      this.visibleColumns[col.key] = true
    })
  }

  // Drag and Drop Column Reordering Handlers
  onDragStart(event: DragEvent, index: number) {
    this.draggedIndex = index
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
    }
  }

  onDrop(event: DragEvent, targetIndex: number) {
    if (this.draggedIndex !== null && this.draggedIndex !== targetIndex) {
      const movedItem = this.localColumns.splice(this.draggedIndex, 1)[0]
      this.localColumns.splice(targetIndex, 0, movedItem)
    }
    this.draggedIndex = null
  }

  // Tab Helpers
  getTabKey(tab: string | { key: string; label: string }): string {
    return typeof tab === 'string' ? tab : tab.key
  }

  getTabLabel(tab: string | { key: string; label: string }): string {
    return typeof tab === 'string' ? tab : tab.label
  }

  switchTab(tabKey: string) {
    this.activeTab = tabKey
    this.$emit('tab-change', tabKey)
    this.applyFilters()
  }

  // Checkbox selection methods
  toggleSelectAll() {
    if (this.isAllSelected) {
      this.selectedRows = []
    } else {
      this.selectedRows = [...this.currentData]
    }
  }

  isRowSelected(item: any): boolean {
    return this.selectedRows.some(row => row[this.rowKey] === item[this.rowKey])
  }

  toggleRowSelection(item: any) {
    const index = this.selectedRows.findIndex(row => row[this.rowKey] === item[this.rowKey])
    if (index > -1) {
      this.selectedRows.splice(index, 1)
    } else {
      this.selectedRows.push(item)
    }
  }

  setItemsPerPage(size: number) {
    this.itemsPerPage = size
    this.applyFilters()
  }

  // Fetch Logic
  async fetchData(): Promise<void> {
    this.loading = true
    try {
      const params = this.buildRequestParams()
      const response = await fetch(`${this.apiEndpoint}?${new URLSearchParams(params)}`)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      this.tableData = await response.json()
      
      if (this.tableData?.meta) {
        this.currentSort = {
          column: this.tableData.meta.sort_column || '',
          direction: (this.tableData.meta.sort_direction as 'asc' | 'desc') || 'asc'
        }
      }
      
      this.$emit('data-change', this.tableData)
    } catch (error) {
      console.error('Error fetching table data:', error)
    } finally {
      this.loading = false
    }
  }

  buildRequestParams(): Record<string, string> {
    const params: Record<string, string> = {
      per_page: this.itemsPerPage.toString(),
      ...this.additionalParams
    }

    if (this.searchQuery) {
      params.search = this.searchQuery
    }

    if (this.startDate) {
      params.start_date = this.startDate
    }

    if (this.endDate) {
      params.end_date = this.endDate
    }

    if (this.activeTab) {
      params.tab = this.activeTab
    }

    if (this.currentSort.column) {
      params.sort_column = this.currentSort.column
      params.sort_direction = this.currentSort.direction
    }

    if (this.tableData?.pagination.current_page) {
      params.page = this.tableData.pagination.current_page.toString()
    }

    // Add dropdown filters
    Object.entries(this.selectedFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params[`filter_${key}`] = Array.isArray(value) ? value.join(',') : value.toString()
      }
    })

    return params
  }

  debounceSearch(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.searchTimeout = window.setTimeout(() => {
      this.applyFilters()
    }, 300)
  }

  applyFilters(): void {
    if (this.tableData) {
      this.tableData.pagination.current_page = 1
    }
    this.fetchData()
  }

  sortBy(column: string): void {
    const columnConfig = this.columns.find(col => col.key === column)
    if (columnConfig?.sortable === false) return

    if (this.currentSort.column === column) {
      this.currentSort.direction = this.currentSort.direction === 'asc' ? 'desc' : 'asc'
    } else {
      this.currentSort.column = column
      this.currentSort.direction = 'asc'
    }
    
    this.applyFilters()
    this.$emit('sort', { column, direction: this.currentSort.direction })
  }

  goToPage(page: number): void {
    if (!this.tableData) return
    
    if (page >= 1 && page <= this.tableData.pagination.last_page) {
      this.tableData.pagination.current_page = page
      this.fetchData()
    }
  }

  getFilterDisplayText(filterKey: string, filterOptions: any[]): string {
    const selected = this.selectedFilters[filterKey]
    const label = this.formatFilterLabel(filterKey)
    
    if (!selected) return label
    
    if (Array.isArray(selected)) {
      return selected.length > 0 ? `${label} (${selected.length})` : label
    }
    
    const option = filterOptions.find(opt => (opt.value || opt) === selected)
    return option ? (option.label || option) : label
  }

  formatFilterLabel(key: string): string {
    return key.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  getFilterOptionLabel(key: string, value: any): string {
    const filterOptions = this.serverFilters[key] || []
    const option = filterOptions.find(opt => (opt.value || opt) === value)
    return option ? (option.label || option) : value
  }

  isFilterSelected(filterKey: string, value: any): boolean {
    const selected = this.selectedFilters[filterKey]
    if (Array.isArray(selected)) {
      return selected.includes(value)
    }
    return selected === value
  }

  setSingleFilter(filterKey: string, value: any): void {
    this.selectedFilters[filterKey] = value
    this.applyFilters()
  }

  clearFilter(filterKey: string): void {
    delete this.selectedFilters[filterKey]
    this.applyFilters()
  }

  clearSearch(): void {
    this.searchQuery = ''
    this.applyFilters()
  }

  clearStartDate(): void {
    this.startDate = ''
    this.applyFilters()
  }

  clearEndDate(): void {
    this.endDate = ''
    this.applyFilters()
  }

  clearAllFilters(): void {
    this.searchQuery = ''
    this.startDate = ''
    this.endDate = ''
    this.selectedFilters = {}
    this.applyFilters()
  }

  formatCurrency(amount: number): string {
    return (amount || 0).toLocaleString()
  }

  formatDate(date: string | Date): string {
    if (!date) return ''
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  formatFilterDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  getBadgeClass(value: string): string {
    const badgeMap: Record<string, string> = {
      'active': 'bg-success bg-opacity-10 text-success',
      'inactive': 'bg-danger bg-opacity-10 text-danger',
      'pending': 'bg-warning bg-opacity-10 text-warning',
      'completed': 'bg-primary bg-opacity-10 text-primary'
    }
    return badgeMap[value?.toLowerCase()] || 'bg-secondary bg-opacity-10 text-secondary'
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj)
  }

  getRowKey(item: any, index: number): string | number {
    return this.getNestedValue(item, this.rowKey) || index
  }

  refresh(): void {
    this.fetchData()
  }

  reset(): void {
    this.searchQuery = ''
    this.startDate = ''
    this.endDate = ''
    this.selectedFilters = {}
    this.currentSort = { column: '', direction: 'asc' }
    this.itemsPerPage = this.defaultItemsPerPage
    this.fetchData()
  }
}

export default toNative(EnhancedTableComponent)
</script>

<style scoped>
/* Main Card Style */
.custom-table-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
}

/* Full Width Emulated Tab Underline Design */
.tabs-container {
  border-bottom: 1px solid #e5e7eb;
  margin-left: -24px;
  margin-right: -24px;
  padding-left: 24px;
  padding-right: 24px;
}
.active-tab-style {
  color: #111827 !important;
}
.active-tab-line {
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #111827; /* Dark black border matching active tab in screenshot */
  border-radius: 99px;
}
.nav-link {
  cursor: pointer;
  font-size: 0.9rem;
  transition: color 0.15s ease-in-out;
}
.nav-link:hover:not(.active-tab-style) {
  color: #4b5563 !important;
}

/* Control Elements: Heights and Alignments aligned exactly like image reference */
.search-input-group {
  border-color: #d1d5db !important;
  border-radius: 6px;
  background-color: #ffffff;
  width: 220px;
  height: 38px;
  display: inline-flex;
  align-items: center;
}
.search-input-group .input-group-text {
  border: 0;
  background-color: transparent;
  padding-right: 0;
}
.search-input-group .form-control {
  font-size: 0.875rem;
  height: 100%;
  border: 0;
  box-shadow: none;
  background-color: transparent;
}
.date-range-picker {
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  min-width: 140px;
  height: 38px;
  padding: 0.375rem 0.75rem;
  background-color: #ffffff;
}
.filter-btn, .customize-columns-btn {
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  color: #4b5563;
  background-color: #ffffff;
  border-radius: 6px;
  height: 38px;
  padding: 0.375rem 0.75rem;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s ease-in-out;
}
.filter-btn:hover, .customize-columns-btn:hover, .filter-icon-btn:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
  color: #111827;
}

.filter-icon-btn {
  width: 38px;
  height: 38px;
  font-size: 0.9rem;
  border: 1px solid #d1d5db;
  color: #4b5563;
  background-color: #ffffff;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-in-out;
}

/* Customize Columns Menu */
.customize-columns-dropdown-menu {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* Table Style */
.custom-styled-table {
  width: 100%;
  border-collapse: collapse;
}
.bg-light-header {
  background-color: #f1f5f9 !important; /* Distinct light gray header */
}
.custom-styled-table th {
  border-bottom: 1px solid #e5e7eb;
  color: #4b5563;
  font-size: 0.8rem;
  letter-spacing: 0.025em;
  text-transform: uppercase;
}
.custom-styled-table th,
.custom-styled-table td {
  border: 1px solid #e5e7eb !important; /* Subtle grid borders around every cell */
}
.table-row-hover:hover {
  background-color: #f1f5f9 !important;
  transition: background-color 0.1s ease-in-out;
}

/* Pagination arrows matching contacts image */
.pagination-arrow-btn {
  color: #9ca3af;
  padding: 0.25rem 0.5rem;
  transition: color 0.15s ease-in-out;
}
.pagination-arrow-btn:hover:not(:disabled) {
  color: #111827;
}
.pagination-arrow-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.items-per-page-toggle {
  color: #9ca3af;
  font-size: 0.85rem;
  padding: 0;
  transition: color 0.15s ease-in-out;
}
.items-per-page-toggle:hover {
  color: #111827;
}
.text-underline {
  text-decoration: underline !important;
}

/* Table Update Loading State */
.table-updating {
  opacity: 0.6;
  pointer-events: none;
  transition: opacity 0.15s ease-in-out;
}

/* Loading Progress Bar at the top of the table */
.loading-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #0d6efd 25%, #60a5fa 50%, #0d6efd 75%);
  background-size: 200% 100%;
  animation: loading-bar-animation 1.5s infinite linear;
}

@keyframes loading-bar-animation {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Alert Banner */
.alert-banner {
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  color: #c2410c;
}
.alert-icon-container {
  color: #ea580c;
  font-size: 1.15rem;
}

/* Visibility Cog Settings */
.hover-bg-light:hover {
  background-color: #f3f4f6;
}
.cursor-grab {
  cursor: grab;
}
.dropdown-item-custom:hover {
  background-color: #f9fafb;
}
</style>